import moment from 'moment';
import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { PORTFOLIO_TABS_NAME } from '../constants/reports/reports-data';
import { RISK_TOKEN_LIST, VENUE_TYPE_LIST } from '../constants/reports/reports-filters';

import {
  DATA_DISPLAY_KEYS,
  DataParent,
  IAccountOption,
  PortfolioManagementContext,
} from './portfolio-management';
import { RiskFiltersContext } from './risk-management/risk-filters-provider';
import { WebSocketContext } from './websocket-context/websocket-provider';

interface IParamsProvider {
  children: ReactNode;
}

export interface ISortPnlTable {
  id: string;
  label: string;
}

interface IDataPnlParams {
  accounts: IAccountOption | IAccountOption[];
  periodDate: string[];
  riskToken?: string;
}

interface IHandleResponseParams {
  response: any;
  accounts: IAccountOption | IAccountOption[];
  pnlRiskTokenParam: string;
  exchange?: string;
  dataParent: DataParent;
}

interface IProvider {
  legacyPnlData: any;
  setLegacyPnlData: (value: any) => void;
  pnlData: any;
  setPnlData: (value: any) => void;
  sortPnlTable: ISortPnlTable[];
  setSortPnlTable: Dispatch<React.SetStateAction<ISortPnlTable[]>>;
  getDataPnlLegacy: (
    account: IAccountOption | IAccountOption[],
    riskToken?: string,
    exchange?: string,
  ) => void;
  getDataPnl: (params: IDataPnlParams) => void;
  isLoadingPnl: boolean;
  filters: {
    pnlRiskToken: string;
    setPnlRiskToken: Dispatch<React.SetStateAction<string>>;
    expandableRows: any[];
    expandableColums: any[];
    pnlVenue: string;
    setExpandableRows: Dispatch<React.SetStateAction<any[]>>;
    setExpandableColums: Dispatch<React.SetStateAction<any[]>>;
    setPnlVenue: Dispatch<React.SetStateAction<string>>;
    datetime: [string, string];
    setDatetime: Dispatch<React.SetStateAction<[string, string]>>;
    datetimeStrings: [string, string];
    setDatetimeStrings: Dispatch<React.SetStateAction<[string, string]>>;
    time: string;
    setTime: Dispatch<React.SetStateAction<string>>;
  };
  isFirstLoadData: React.MutableRefObject<boolean>;
}

const PNL_KEY = 'PNL';
const PNL_LEGACY_KEY = 'PNL_LEGACY';

export const PNLContext = createContext<IProvider>({} as IProvider);

export const PNLProvider = (props: IParamsProvider) => {
  const { subscribeToChannel, sendMessage } = useContext(WebSocketContext);
  const {
    addDataToAccount,
    addGeneralAggregatedData,
    selectedAccountOption,
    selectedDataDisplayOption,
  } = useContext(PortfolioManagementContext);
  const {
    filters: { riskToken },
  } = useContext(RiskFiltersContext);
  const [accountStates, setAccountStates] = useState<{ [key: string]: any }>({});
  const [legacyPnlData, setLegacyPnlData] = useState<any>(null);
  const [pnlData, setPnlData] = useState<any>(null);
  const [isLoadingPnl, setIsLoadingPnl] = useState(false);
  const [pnlRiskToken, setPnlRiskToken] = useState<string>(RISK_TOKEN_LIST[0].value);
  const [expandableRows, setExpandableRows] = useState<any[]>([]);
  const [expandableColums, setExpandableColums] = useState<any[]>([]);
  const [pnlVenue, setPnlVenue] = useState(VENUE_TYPE_LIST[0].value);
  const today = moment().format('YYYY/MM/DD');
  const yesterday = moment().subtract(1, 'day').format('YYYY/MM/DD');

  const [datetime, setDatetime] = useState<[string, string]>([yesterday, today]);
  const [datetimeStrings, setDatetimeStrings] = useState<[string, string]>([yesterday, today]);
  const [time, setTime] = useState<string>('24');
  const [sortPnlTable, setSortPnlTable] = useState<ISortPnlTable[]>([
    {
      id: 'dates',
      label: 'Selected dates',
    },
    {
      id: 'books',
      label: 'Books',
    },
    {
      id: 'tokens',
      label: 'Tokens',
    },
  ]);

  const datetimeRef = useRef<[string, string]>(['', '']);
  const isFirstLoadData = useRef<boolean>(true);

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const handleResponse = ({
    response,
    accounts,
    pnlRiskTokenParam,
    exchange,
    dataParent,
  }: IHandleResponseParams) => {
    if (!response) {
      setIsLoadingPnl(false);
      return;
    }

    const isPnlResponse = response?.id === PNL_KEY || response?.id === PNL_LEGACY_KEY;
    if (!isPnlResponse) {
      setIsLoadingPnl(false);
      return;
    }

    const isError = Boolean(response.error);
    const data = !isError ? response.result : [];
    const bookErrors = !isError ? response.result?.errors : [];

    if (Array.isArray(accounts)) {
      if (exchange) {
        addGeneralAggregatedData({
          dataParent,
          data,
          error: isError,
          exchange,
          pnlRiskToken: pnlRiskTokenParam,
          bookErrors,
        });
      }
    } else {
      addDataToAccount({
        account: accounts,
        dataParent,
        data,
        error: isError,
        pnlRiskToken: pnlRiskTokenParam,
        bookErrors,
      });
    }

    setIsLoadingPnl(false);
  };

  const getDataPnlLegacy = useCallback(
    async (
      accounts: IAccountOption | IAccountOption[],
      riskToken: string = 'quote',
      exchange?: string,
    ) => {
      const config = {
        risk_token: riskToken.toUpperCase(),
      };

      let credentials;
      if (Array.isArray(accounts)) {
        credentials = accounts.map((account) => ({ name: account.name }));
      } else {
        credentials = { name: accounts.name };
      }

      const params = {
        jsonrpc: '2.0',
        id: PNL_LEGACY_KEY,
        method: 'pnl_explain',
        params: {
          config,
          credentials,
        },
      };

      setIsLoadingPnl(true);
      sendMessage(JSON.stringify(params), true);
      subscribeToChannel(PNL_LEGACY_KEY, (response: any) =>
        handleResponse({
          response,
          accounts,
          pnlRiskTokenParam: riskToken,
          exchange,
          dataParent: PORTFOLIO_TABS_NAME.pnlLegacy as DataParent,
        }),
      );
    },
    [sendMessage, setIsLoadingPnl, pnlRiskToken],
  );

  const getDataPnl = useCallback(
    ({ accounts, periodDate, riskToken = 'quote' }: IDataPnlParams) => {
      let periodDateList = periodDate;
      if (periodDateList.length === 0) {
        const [start, end] = datetimeRef.current;
        periodDateList = [moment(start).format('YYYY-MM-DD'), moment(end).format('YYYY-MM-DD')];
      }

      const config = {
        start_date: periodDateList[0],
        end_date: periodDateList[1],
        eod_hour: Number(time),
        risk_token: riskToken.toUpperCase(),
      };

      const accountList = Array.isArray(accounts) ? accounts : [accounts];

      const sources = accountList.map(({ exchange, venue, name }) =>
        exchange === 'otc' ? { venue, account: name } : { name },
      );

      const exchange = accountList[0].exchange;

      const params = {
        jsonrpc: '2.0',
        id: PNL_KEY,
        method: 'run_pnl_engine',
        params: {
          config,
          sources,
        },
      };

      setIsLoadingPnl(true);
      sendMessage(JSON.stringify(params));
      subscribeToChannel(PNL_KEY, (response: any) =>
        handleResponse({
          response,
          accounts,
          pnlRiskTokenParam: riskToken,
          exchange,
          dataParent: PORTFOLIO_TABS_NAME.pnl as DataParent,
        }),
      );
    },
    [sendMessage, setIsLoadingPnl, pnlRiskToken, time],
  );

  const applyAccountState = (accountState: any) => {
    setPnlRiskToken(accountState.pnlRiskToken || RISK_TOKEN_LIST[0].value);
    setExpandableRows(accountState.expandableRows || []);
    setExpandableColums(accountState.expandableColums || []);
    setPnlVenue(accountState.pnlVenue || VENUE_TYPE_LIST[0].value);
    setDatetime(accountState.datetime || [yesterday, today]);
    setDatetimeStrings(accountState.datetimeStrings || [yesterday, today]);
    setTime(accountState.time || '24');
  };

  const applyDefaultState = () => {
    isFirstLoadData.current = true;
    setPnlRiskToken(RISK_TOKEN_LIST[0].value);
    setExpandableColums([]);
    setPnlVenue(VENUE_TYPE_LIST[0].value);
    setDatetime([yesterday, today]);
    setDatetimeStrings([yesterday, today]);
    setTime('24');
  };

  useEffect(() => {
    if (!isDataDisplayPerAccount) return;
    if (!selectedAccountOption) return;

    const accountState = accountStates[selectedAccountOption.name];
    if (accountState) {
      applyAccountState(accountState);
    } else {
      applyDefaultState();
    }
  }, [isDataDisplayPerAccount, selectedAccountOption, selectedDataDisplayOption]);

  useEffect(() => {
    if (isDataDisplayPerAccount) return;

    const accountState = accountStates['pnl-aggregated'];
    if (accountState) {
      applyAccountState(accountState);
    } else {
      applyDefaultState();
    }
  }, [selectedDataDisplayOption]);

  useEffect(() => {
    datetimeRef.current = datetime;

    const nextState = {
      pnlRiskToken,
      expandableRows,
      expandableColums,
      pnlVenue,
      datetime,
      datetimeStrings,
      time,
    };

    const key =
      selectedAccountOption && isDataDisplayPerAccount
        ? selectedAccountOption.name
        : 'pnl-aggregated';

    setAccountStates((prev) => ({
      ...prev,
      [key]: nextState,
    }));
  }, [
    selectedAccountOption,
    expandableRows,
    expandableColums,
    pnlRiskToken,
    pnlVenue,
    datetime,
    datetimeStrings,
    time,
  ]);

  useEffect(() => {
    if (!riskToken) return;

    setPnlRiskToken(riskToken);
  }, [riskToken]);

  const filters = {
    pnlRiskToken,
    setPnlRiskToken,
    expandableRows,
    expandableColums,
    setExpandableRows,
    setExpandableColums,
    pnlVenue,
    setPnlVenue,
    datetime,
    setDatetime,
    datetimeStrings,
    setDatetimeStrings,
    time,
    setTime,
  };

  return (
    <PNLContext.Provider
      value={{
        legacyPnlData,
        setLegacyPnlData,
        sortPnlTable,
        setSortPnlTable,
        pnlData,
        setPnlData,
        getDataPnl,
        getDataPnlLegacy,
        isLoadingPnl,
        isFirstLoadData,
        filters,
      }}
    >
      {props.children}
    </PNLContext.Provider>
  );
};
